import { Alert } from "@texada/ui";
import { ALERT_ERROR_TIMEOUT, AVAILABILITY_URL } from "constants/AppConstants";
import {
  ASSET_STATUS,
  getApiErrorMsg,
  TRANSACTION_TYPE,
  SrmBoolean,
  ASSET_TYPE,
  hasArrayWithValue,
  IInventoryDivision,
  ISystemInfo,
  IAsset,
  DOCUMENT_TYPE,
  METER_TYPE
} from "helpers/helper";
import { PRODUCT_MODE } from "./sections/contractproductselect/ProductServiceUtils";
import { FlaskReq } from "helpers/Request";
import { SRM_DATE_FORMAT, SRM_TIME_FORMAT } from "helpers/timeHelpers";
import { operatorPermissionUrl } from "helpers/urls";
import { getUDFsPostData } from "helpers/hooks";
import i18n from "i18n/i18n";
import {
  isEmpty,
  omit,
  toNumber,
  pick,
  cloneDeep,
  capitalize,
  isNumber,
  head,
  get,
  isNil
} from "lodash";
import moment from "moment";
import axios from "axios";
import React from "react";
import { ICustomSRMForm } from "pages/settings/customFields/form/common";
import { FIELD_TYPE } from "components/commons/customizedfields/CustomizedFields";

const DATE_FORMAT = "DD/MM/YYYY h:mm A";

const initialState: any = {
  startDate: moment().format(DATE_FORMAT),
  endDate: moment().add(1, "days").format(DATE_FORMAT),
  document_histories: [],
  location: "",
  date: moment().format(SRM_DATE_FORMAT),
  contractNumber: "____",
  comments: [],
  saleDiscP: 0,
  rentalDiscP: 0,
  site: {},
  services: [],
  errors: {},
  hasFormBeenSubmitted: false,
  edit: false,
  update: false,
  isChangingView: false,
  dateRange: {},
  loading: false,
  isModalOpen: false,
  isSigOptModalOpen: false,
  sigStepStartFrom: null,
  modalType: "",
  modalError: "",
  recipientEmail: "",
  showConfirmModal: false,
  dateOut: moment().format(SRM_DATE_FORMAT),
  timeOut: moment().format(SRM_TIME_FORMAT),
  timeOutEdited: false,
  dateIn: moment().format(SRM_DATE_FORMAT),
  timeIn: moment().add(30, "minutes").format(SRM_TIME_FORMAT),
  timeInEdited: false,
  contract: {},
  taxes: [],
  division: "",
  isBtnDisabled: false,
  products: [],
  blanketPo: "",
  isSendForPayment: false,
  editPermission: true,
  indefiniteRental: false,
  poNumber: "",
  createDeliveryTicket: false,
  taxCodes: [{ tax: "", exemptionNumber: "", exemptionType: "" }],
  linkDocuments: [],
  poRequired: false,
  documentHistories: [],
  attachmentList: [],
  hasSigned: false,
  productTotal: 0,
  serviceTotal: 0,
  chargeWaiver: SrmBoolean.False,
  isFetchHistory: false,
  damageWaiverListData: [],
  allowEditWaiverPercent: false,
  isDraft: false,
  selectedDivisionConfig: {
    defaultDateRng: 0,
    hoursDays: "",
    timeOut: ""
  },
  customizedFields: [],
  UDFs: {},
  isUDFsLoaded: false,
  meterInformation: {},
  serialInformation: {},
  rentalPeriodSecurity: null
};

type errorTypes = {
  poNumber?: string;
  customer?: string;
  products?: string;
  services?: string;
  nameChecklist?: string;
};

interface IContractContext {
  state: {
    [key: string]: any;
    startDate: string;
    endDate: string;
    dateOut: string;
    timeOut: string;
    dateIn: string;
    timeIn: string;
    location: string;
    customizedFields: Array<any>;
    UDFs: Record<string, any>;
  };
  dispatch: React.Dispatch<any>;
  setState: (payload: any) => any;
  getContract?: any;
  fetchProducts?: () => void;
  isLoading?: boolean;
  setIsLoading?: any;
  setAttachmentList?: (payload: any) => void;
  systemInfo?: any;
  contractMode?: ContractMode;
  togglePageMode?: () => void;
}

const ContractContext = React.createContext<IContractContext>(undefined);
ContractContext.displayName = "ContractContext";

const contractReducer = (state, action): typeof initialState => {
  switch (action.type) {
    case "RESET":
      return { ...initialState };
    case "SET_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

function checkCanEditRentalPeriod(product: Models.ContractDetail) {
  if (!product) return false;
  if (
    product.status === ASSET_STATUS.OnRent ||
    product.status === ASSET_STATUS.OffRent
  ) {
    return isEmpty(product.dateBilledThru) && isEmpty(product.timeBilledThru);
  }
}

function getFullAddress(data) {
  return `${data.address1} ${data.address2} ${data.city} ${
    data.provState || data.province
  } ${data.postalCode || data.postal_zip || data.postalZip}`;
}

enum ContractMode {
  VIEW = "view",
  EDIT = "edit",
  CREATE = "create",
  DRAFT = "draft"
}

const validateQuantity = products => {
  let error = "";
  for (let i = 0; i < products.length; i++) {
    if (!isFinite(toNumber(products[i].quantity))) {
      error = i18n.t("invalid_product_or_service_quantity_entered");
      Alert.error({
        message: error
      });
      return error;
    }
    if (toNumber(products[i].quantity) <= 0) {
      error = i18n.t("product_or_service_quantity_cannot_be_zero_or_negative");
      Alert.error({
        message: error
      });
      return error;
    }
  }
  return error;
};

const wasUnavailableProductAdded = (division, products, allWarehouses) => {
  let unavailableProds = [];
  const { warehouse } = allWarehouses.find(
    ({ warehouse }) => warehouse.srm_uid === division.trim()
  );

  if (warehouse.add_unvailable_prod_contract) return false;

  products.forEach(prod => {
    if (prod.availability < 1) {
      unavailableProds.push(prod.product);
    }
  });

  if (unavailableProds.length === 0) return false;
  Alert.error({
    message: `Unavailable products cannot be added to ${
      warehouse.title
    }: ${unavailableProds.join(", ")}.`
  });

  return true;
};

function getProducts(ref, state, isUpdateDraft = false) {
  let products;
  if (ref.current) {
    products = ref.current.getSelectedProducts;
  } else {
    products = [...state.prevProducts];
  }

  products = products
    .concat(
      state.services.map(s => {
        s.type = TRANSACTION_TYPE.SERVICE;
        s.product = s.serviceCode || s.product;
        s.serviceCode = s.serviceCode.trim();
        return s;
      })
    )
    .map(detail => {
      let formatted = {
        ...detail,
        dateOut: detail.dateOut || state.dateOut,
        timeOut: detail.timeOut || state.timeOut,
        dateIn: detail.dateIn || state.dateIn,
        timeIn: detail.timeIn || state.timeIn
      };
      if (detail.hasOwnProperty("parentKitNumber")) {
        formatted.kitDetails = products
          .filter(e => detail.parentKitNumber === e.childKitNumber)
          .map(kitDetail => ({
            ...pick(kitDetail, [
              "itemType",
              "itemNumber",
              "itemDescription",
              "quantity",
              "chargeable",
              "discountP",
              "print",
              "add"
            ])
          }));
      }
      return formatted;
    });

  if (isUpdateDraft) {
    return products;
  }

  return products.filter(p => {
    // filter out changed/deleted details without a detailTrN (e.g. auto-added services)
    const isInvalid =
      [PRODUCT_MODE.DELETE, PRODUCT_MODE.EDIT].includes(p.mode) &&
      [undefined, ""].includes(p.detailTrxN);

    return !isInvalid && !p.hasOwnProperty("childKitNumber");
  });
}

const INTERNAL_TICKET = "I";
async function getPostData(state, products = [], isDraft = false) {
  let postData = {
    ...omit(
      state,
      "errors",
      "selectedDivisionConfig",
      "hasFormBeenSubmitted",
      "comments_customer",
      "comments_internal",
      "site",
      "isChangingView",
      "edit",
      "dateRange",
      "services",
      "contractNumber",
      "loading",
      "isModalOpen",
      "contract",
      "taxes",
      "prevProducts",
      "customer",
      "taxCodes",
      "details",
      "products",
      "comments",
      "linkDocuments",
      "notes",
      "commentsList",
      "prevContract",
      "selectedDivision",
      "selectedLocation",
      "document_histories",
      "isSigOptModalOpen",
      "showConfirmModal",
      "isBtnDisabled",
      "isSendForPayment",
      "editPermission",
      "createDeliveryTicket",
      "documentHistories",
      "attachmentList",
      "UDFs",
      "customizedFields",
      "salesmanCode"
    ),
    customerNumber: state?.customer?.customer_number,
    date: moment().format(SRM_DATE_FORMAT),
    // if every value in deliverTo is empty set it as undefined
    deliverTo: Object.values(state.deliverTo || {}).every(v => isEmpty(v))
      ? undefined
      : state.deliverTo,
    pickUpFrom: Object.values(state.pickUpFrom || {}).every(v => isEmpty(v))
      ? undefined
      : state.pickUpFrom,
    deliveryWeight: state.deliveryWeight || undefined,
    createDeliveryTicket: state.createDeliveryTicket ? INTERNAL_TICKET : "",
    dateOut: state.dateOut,
    timeOut: state.timeOut,
    dateIn: state.dateIn,
    timeIn: state.timeIn,
    indefiniteRental: state?.indefiniteRental
      ? SrmBoolean.True
      : SrmBoolean.False,
    UDFs: getUDFsPostData(
      state?.UDFs,
      isDraft
        ? DOCUMENT_TYPE.WORKSHEET.toUpperCase()
        : DOCUMENT_TYPE.CONTRACT.toUpperCase()
    )
  };

  let details = cloneDeep(products);
  if (state.taxCodes.length > 0) {
    let taxNumber = 0;
    state.taxCodes.forEach(e => {
      if (isEmpty(e.tax)) return;
      taxNumber++;
      if (e.tax) {
        postData[`defaultTax${taxNumber}Code`] = e.tax;
      }
      postData[`tax${taxNumber}ExemptNo`] = e.exemptionNumber;
      postData[`tax${taxNumber}ExemptType`] = e.exemptionType;
      details = details.map(d => ({ ...d, [`tax${taxNumber}Code`]: e.tax }));
    });
  }

  if (details.length > 0) {
    postData.details = details
      .filter(
        e =>
          [ASSET_TYPE.Sales, ASSET_TYPE.Rental].includes(e.type) ||
          e.mode === PRODUCT_MODE.ADD
      )
      .map(e => {
        let productData = { ...e };
        if (state.meterInformation[e?.product?.trim()]) {
          productData = {
            ...productData,
            ...state.meterInformation[e.product.trim()]
          };
        }

        if (state.serialInformation[e?.product?.trim()]) {
          productData = {
            ...productData,
            serialNumber: state.serialInformation[e.product.trim()], // for contract API
            serialNos: [
              {
                serialNo: state.serialInformation[e.product.trim()] // for draft API
              }
            ]
          };
        }

        if (
          state?.poLinkInformation &&
          e?.product &&
          state.poLinkInformation[e.product.trim()]
        ) {
          productData = {
            ...productData,
            createPurchaseOrderInfo: state.poLinkInformation[e.product.trim()]
          };
        }

        if (
          isDraft &&
          e.type === ASSET_TYPE.Sales &&
          isNumber(state.saleDiscP) &&
          !isNumber(productData.discP)
        ) {
          productData.discP = state.saleDiscP;
        }

        if (
          isDraft &&
          e.type === ASSET_TYPE.Rental &&
          isNumber(state.rentalDiscP)
        ) {
          productData.discP = state.rentalDiscP;
        }

        if (e.hasOwnProperty("rates") && e.hasOwnProperty("rateOverride")) {
          delete productData["rates"];
        }

        if (
          e.type === ASSET_TYPE.Sales &&
          Number(e.listPrice) === Number(e.originPrice)
        ) {
          return { ...omit(productData, ["listPrice", "originPrice"]) };
        }
        return { ...productData };
      });
  }

  if (!isEmpty(state.salesmanCode)) {
    postData.salesmanCode = state.salesmanCode;
  }
  return postData;
}

async function updateCustomerDiscount(state) {
  if (!state.saveDiscountToCustomer || !state.customerNumber) {
    return;
  }

  try {
    await FlaskReq(
      {
        url: `${localStorage.flask_url}/customers/${encodeURIComponent(
          state.customerNumber
        )}`,
        method: "put",
        data: {
          rental_discount_rate: Number(state.rentalDiscP || 0),
          sales_discount_rate: Number(state.saleDiscP || 0),
          province: state?.customer?.province
        }
      },
      {
        showMessage: false
      }
    );
  } catch (err) {
    Alert.error({
      message: getApiErrorMsg(err),
      timeout: ALERT_ERROR_TIMEOUT
    });
  }
}

const showPromptOnChangeRouteOnCreate = ({ state, data }) => {
  const errorList = [
    ...(state.poRequired ? [isEmpty(data.poNumber)] : []),
    isEmpty(data["division"]),
    isEmpty(data["location"]),
    isEmpty(state?.salesmanName),
    isEmpty(state?.customer),
    data.products.length <= 0
  ];

  return errorList.every(item => item === true) ? false : true;
};

const validateOnCreate =
  (state, props, customerRef, systemInfo, isValidateUDFS = true) =>
  async data => {
    let errors: errorTypes = {};

    if (state.poRequired && isEmpty(data.poNumber)) {
      errors.poNumber = i18n.t("field_is_required", {
        field: i18n.t("purchase_order")
      });
      Alert.error({
        message: errors.poNumber
      });
    }
    ["division", "location"].forEach(key => {
      if (isEmpty(data[key])) {
        errors[key] = i18n.t("field_is_required", { field: capitalize(key) });
        Alert.error({
          message: errors[key]
        });
      }
    });

    if (
      (isEmpty(state?.salesmanName) || isEmpty(state?.salesmanCode?.trim())) &&
      systemInfo?.mandatory_salesman === SrmBoolean.True
    ) {
      errors.salesmanCode = i18n.t("field_is_required", {
        field: i18n.t("salesperson")
      });
      Alert.error({
        message: errors.salesmanCode
      });
    }
    if (isEmpty(state?.customer)) {
      errors.customer = i18n.t("field_is_required", {
        field: i18n.t("customer")
      });
      Alert.error({
        message: errors.customer
      });
    }
    if (
      isEmpty(state?.nameChecklist?.trim()) &&
      systemInfo?.verify_name_on_customer === SrmBoolean.True
    ) {
      errors.nameChecklist = i18n.t("field_is_required", {
        field: i18n.t("name_checklist")
      });
      Alert.error({
        message: errors.nameChecklist
      });
    }
    if (data.products.length <= 0) {
      errors.products = i18n.t("field_is_required", {
        field: i18n.t("products")
      });
      Alert.error({
        message: errors.products
      });
    }

    const productError = validateQuantity(data.products);
    if (productError) {
      errors.products = productError;
    }

    const serviceError = validateQuantity(data.services);
    if (serviceError) {
      errors.services = serviceError;
    }

    let productAvailabilityError = false;
    if (data.products.length > 0) {
      productAvailabilityError = wasUnavailableProductAdded(
        state.division,
        data.products,
        props.allWarehouses
      );
      if (productAvailabilityError) {
        errors.products = "errors";
      }
    }

    if (
      state.chargeWaiver === SrmBoolean.True &&
      isEmpty(state.damageWaiverCode) &&
      data.products.length > 0
    ) {
      errors["damageWaiver"] = i18n.t("field_is_required", {
        field: i18n.t("damage_waiver")
      });
      Alert.error({
        message: errors["damageWaiver"]
      });
    }

    if (isValidateUDFS) {
      errors = validateUDFS({ errors, state, data });
    }

    if (!isEmpty(errors)) return errors;

    const creditError = await customerRef.current.getCreditRating(
      state.customer.customer_number,
      Number(state.productTotal) + Number(state.serviceTotal),
      true
    );
    if (creditError) {
      errors["creditChecking"] = false;
    }
    return errors;
  };

const validateOnEdit = (
  state,
  props,
  data,
  systemInfo,
  isValidateUDFS = true
) => {
  let errors: errorTypes = {};

  if (state.poRequired && isEmpty(state.poNumber)) {
    errors.poNumber = i18n.t("field_is_required", {
      field: i18n.t("purchase_order")
    });
    Alert.error({
      message: errors.poNumber
    });
  }

  if (
    isEmpty(state?.salesmanCode?.trim()) &&
    systemInfo?.mandatory_salesman === SrmBoolean.True
  ) {
    errors.salesmanCode = i18n.t("field_is_required", {
      field: i18n.t("salesperson")
    });
    Alert.error({
      message: errors.salesmanCode
    });
  }

  if (
    isEmpty(state?.nameChecklist?.trim()) &&
    systemInfo?.verify_name_on_customer === SrmBoolean.True
  ) {
    errors.nameChecklist = i18n.t("field_is_required", {
      field: i18n.t("name_checklist")
    });
    Alert.error({
      message: errors.nameChecklist
    });
  }

  if (data.products.length <= 0 && data.services.length <= 0) {
    errors.products = i18n.t("field_is_required", {
      field: i18n.t("products")
    });
    Alert.error({
      message: errors.products
    });
  }

  if (
    state.chargeWaiver === SrmBoolean.True &&
    isEmpty(state.damageWaiverCode) &&
    data.products.length > 0
  ) {
    errors["damageWaiver"] = i18n.t("field_is_required", {
      field: i18n.t("damage_waiver")
    });
    Alert.error({
      message: errors["damageWaiver"]
    });
  }

  const productError = validateQuantity(data.products);
  if (productError) {
    errors.products = productError;
  }

  const serviceError = validateQuantity(data.services);
  if (serviceError) {
    errors.services = serviceError;
  }

  let productAvailabilityError = false;
  if (data.products.length > 0) {
    productAvailabilityError = wasUnavailableProductAdded(
      state.division,
      data.products,
      props.allWarehouses
    );
    if (productAvailabilityError) {
      errors.products = "errors";
    }
  }

  if (isValidateUDFS) {
    errors = validateUDFS({ errors, state, data });
  }

  return errors;
};

const allowUnavailableProductHelper = {
  checkLocation: (divisionConfiguration: IInventoryDivision) => {
    return {
      current: divisionConfiguration?.check_location,
      allow: divisionConfiguration?.check_location === SrmBoolean.False,
      notAllow: divisionConfiguration?.check_location !== SrmBoolean.False
    };
  },
  checkOverrideQuantity: (systemInformation: ISystemInfo) => {
    return {
      current: systemInformation?.allow_override_quantity,
      allow: systemInformation?.allow_override_quantity === SrmBoolean.True,
      checkLocationOnHand: systemInformation?.allow_override_quantity === "L"
    };
  },
  hasAssetLocationOnHand: (asset: IAsset) => {
    return asset?.locations.some(location => {
      return location?.onhand > 0;
    });
  }
};

const validateUDFS = ({ state, data, errors }) => {
  const errorObj = { ...errors };

  const requiredUDFS = state?.customizedFields.reduce((newItems, item) => {
    if (item?.detail_list[0]?.required === SrmBoolean.True) {
      return [...newItems, item];
    }
    return newItems;
  }, []);

  requiredUDFS?.forEach(item => {
    const fieldName = item.field_name;
    const value = data?.UDFs[fieldName]?.fields[0]?.value;
    if (!value) {
      errorObj[fieldName] = i18n.t("custom_field_is_required", {
        field: fieldName
      });
      Alert.error({
        message: errorObj[fieldName]
      });
    }
  });

  return errorObj;
};

function generatePermissionMessage(discountPermissionDetails) {
  if (
    discountPermissionDetails?.contractDetailDiscRAccess === SrmBoolean.False &&
    discountPermissionDetails?.contractDetailDiscSAccess === SrmBoolean.False
  ) {
    return i18n.t(
      "user_does_not_have_permission_to_add_rental_and_sales_discount_for_this_division"
    );
  } else if (
    discountPermissionDetails?.contractDetailDiscRAccess === SrmBoolean.False
  ) {
    return i18n.t(
      "user_does_not_have_permission_to_add_rental_discount_for_this_division"
    );
  } else if (
    discountPermissionDetails?.contractDetailDiscSAccess === SrmBoolean.False
  ) {
    return i18n.t(
      "user_does_not_have_permission_to_add_sales_discount_for_this_division"
    );
  }
  return "";
}

const validateProductsAvailabilityAndMeter = async ({
  selectedProducts,
  meterInformation,
  location,
  allowUnavailableProducts,
  checkLocationOnHand,
  setMeterRequiredProducts,
  setSelectedMeterProduct,
  setIsConfirmMeterModalOpen,
  serialInformation = {},
  setSerialRequiredProducts = undefined,
  setSelectedSerialProduct = undefined,
  setIsConfirmSerialModalOpen = undefined
}) => {
  let newMeterRequiredProducts = [];
  let newSerialRequiredProducts = [];
  let isUnavailable = false;
  let isRequireMeter = false;
  let isRequireSerial = false;
  let errorMessageList = [];

  await Promise.allSettled(
    selectedProducts.map(async product => {
      const {
        isUnavailable: newIsUnavailable,
        isRequireMeter: newIsRequireMeter,
        isRequireSerial: newIsRequireSerial,
        error
      } = await validateProductAvailabilityAndMeter(
        product,
        meterInformation,
        serialInformation,
        location,
        allowUnavailableProducts,
        checkLocationOnHand
      );

      isUnavailable = isUnavailable || newIsUnavailable;
      isRequireMeter = isRequireMeter || newIsRequireMeter;
      isRequireSerial = isRequireSerial || newIsRequireSerial;
      if (error) {
        errorMessageList.push(error);
      }

      if (newIsRequireMeter) {
        newMeterRequiredProducts.push(product);
      }

      if (newIsRequireSerial) {
        newSerialRequiredProducts.push(product);
      }

      return;
    })
  );

  if (!isUnavailable && isRequireMeter) {
    setMeterRequiredProducts(newMeterRequiredProducts);
    setSelectedMeterProduct(newMeterRequiredProducts[0]);
    setIsConfirmMeterModalOpen(true);
  }

  if (
    !isUnavailable &&
    !isRequireMeter &&
    setSerialRequiredProducts &&
    isRequireSerial
  ) {
    setSerialRequiredProducts(newSerialRequiredProducts);
    setSelectedSerialProduct(newSerialRequiredProducts[0]);
    setIsConfirmSerialModalOpen(true);
  }

  return {
    isValid: !isUnavailable && !isRequireMeter && !isRequireSerial,
    errorMessageList
  };
};

const validateProductAvailabilityAndMeter = async (
  product,
  meterInformation,
  serialInformation,
  location,
  allowUnavailableProducts,
  checkLocationOnHand
) => {
  const productNumber =
    product?.productNumber?.trim() || product?.product?.trim();
  let isRequireMeter = false;
  let isRequireSerial = false;
  let isUnavailable = false;
  let error = "";

  if (
    product.type === TRANSACTION_TYPE.RENTAL &&
    [METER_TYPE.METERED, METER_TYPE.OLD_METERED, METER_TYPE.BLADED].includes(
      product?.meterType
    ) &&
    !meterInformation.hasOwnProperty(productNumber)
  ) {
    isRequireMeter = true;
  }

  const allowSerialNumbers =
    product.type === TRANSACTION_TYPE.NON_SERIALIZED && product.serialNumbers;

  const serialNumbers = allowSerialNumbers
    ? product.serialNumbers.filter(serial => {
        return (
          !isEmpty(serial?.serialNumber) &&
          serial?.division?.replace(/\s/g, "") ===
            location?.replace(/\s/g, "") &&
          isNil(serial?.dateSold)
        );
      })
    : [];

  if (
    hasArrayWithValue(serialNumbers) &&
    !serialInformation.hasOwnProperty(productNumber)
  ) {
    isRequireSerial = true;
  }

  const res = await axios.get(
    `${AVAILABILITY_URL}${encodeURIComponent(product.product)}/`,
    {
      headers: {
        locOrAll: location,
        responseType: "json"
      }
    }
  );
  try {
    const srmData = JSON.parse(res.data);

    if (allowUnavailableProducts || srmData.productAvailable > 0) {
      return { isUnavailable, isRequireMeter, isRequireSerial, error };
    }

    if (
      checkLocationOnHand &&
      allowUnavailableProductHelper.hasAssetLocationOnHand(product)
    ) {
      return { isUnavailable, isRequireMeter, isRequireSerial, error };
    }

    if (
      srmData.productAvailable <= 0 &&
      srmData?.availabilityNA === SrmBoolean.True
    ) {
      return { isUnavailable, isRequireMeter, isRequireSerial, error };
    }
    error = i18n.t("product_product_number_is_currently_unavailable", {
      productNumber
    });
    return { isUnavailable: true, isRequireMeter, isRequireSerial, error };
  } catch (error) {}

  return { isUnavailable, isRequireMeter, isRequireSerial, error };
};

const draftValidation = ({ state, data, systemInfo }) => {
  const errorList = [
    ...(state.poRequired ? [isEmpty(data.poNumber)] : []),
    isEmpty(data["division"]),
    isEmpty(data["location"]),
    isEmpty(state?.salesmanName),
    isEmpty(state?.customer),
    isEmpty(
      state?.salesmanCode?.trim() &&
        systemInfo?.mandatory_salesman === SrmBoolean.True
    )
  ];

  return errorList.some(item => item === true) ? true : false;
};

const prepareUDFS = udfs => {
  return udfs?.reduce((newObj, value) => {
    const field = head(
      value?.fields
    ) as ICustomSRMForm[string]["fields"][number];
    const fieldType = field?.type;
    return {
      ...newObj,
      [value?.name]: {
        ...value,
        fields: [
          {
            ...field,
            ...(fieldType === FIELD_TYPE.MULTISELECT
              ? {
                  value: (field?.value || []).map(selection => {
                    return {
                      label: selection,
                      value: selection
                    };
                  })
                }
              : {
                  value: head(field?.value)
                })
          }
        ]
      }
    };
  }, {});
};

async function fetchDivisionConfig(setState, user, division) {
  const res = await FlaskReq(
    {
      url: `${localStorage.flask_url}/inventory/divisions/${encodeURIComponent(
        division || " "
      )}/?expand=contract_line_default_print`,
      method: "get"
    },
    { showMessage: false }
  );
  const response = await axios.get(
    operatorPermissionUrl({
      userId: user.srm_uid,
      division: user.compperms.operator_by_division
        ? division || " "
        : undefined
    }),
    {
      responseType: "json"
    }
  );
  setState({
    selectedDivisionConfig: {
      ...res.data,
      ...get(response, "data.operators.[0]", {})
    }
  });
}

const contractSecurityHelper = {
  dateRange: state => {
    return {
      ...(state?.rentalPeriodSecurity?.fromDate
        ? {
            minStartDate: moment(state?.rentalPeriodSecurity?.fromDate)
          }
        : {}),
      ...(state?.rentalPeriodSecurity?.toDate
        ? {
            maxEndDate: moment(state?.rentalPeriodSecurity?.toDate)
          }
        : {})
    };
  }
};

export {
  ContractContext,
  contractReducer,
  initialState,
  checkCanEditRentalPeriod,
  getFullAddress,
  validateQuantity,
  wasUnavailableProductAdded,
  errorTypes,
  getPostData,
  getProducts,
  ContractMode,
  updateCustomerDiscount,
  validateOnCreate,
  validateOnEdit,
  allowUnavailableProductHelper,
  validateUDFS,
  showPromptOnChangeRouteOnCreate,
  generatePermissionMessage,
  validateProductsAvailabilityAndMeter,
  draftValidation,
  prepareUDFS,
  fetchDivisionConfig,
  contractSecurityHelper
};
